// Chart.jsx
// Single chart component — candlestick / line chart
// Per-chart: timeframe, chart type, indicators modal, zoom
// Lazy loading, custom legend, proper IST timezone handling

import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';
import useStore from '../store';
import { fetchCandles, fetchCandlesByRange, fetchCandlesAfter, fetchIndicators, fetchIndicatorsByRange, fetchTradesForChart, fetchLiveCandle, fetchVPatternsForChart } from '../api';
import { buildMarkersFromTrades, applyMarkersToSeries, removeMarkersFromSeries } from './tradeMarkers';

// Build V pattern markers from v_patterns data
function buildVPatternMarkers(vPatterns) {
  if (!vPatterns || vPatterns.length === 0) return [];
  const IST_OFFSET = 5.5 * 60 * 60 * 1000;
  return vPatterns.map(vp => {
    const d   = new Date(vp.pattern_time);
    const t   = Math.floor((d.getTime() + IST_OFFSET) / 1000);
    const isBuy = vp.direction === 'buy';
    return {
      time:     t,
      position: isBuy ? 'belowBar' : 'aboveBar',
      shape:    isBuy ? 'arrowUp'  : 'arrowDown',
      color:    isBuy ? '#26a69a'  : '#ef5350',
      text:     `V${vp.strength || ''}`,
      id:       `vp_${vp.pattern_time}_${vp.ema}`,
    };
  }).sort((a, b) => a.time - b.time);
}

// ============================================================
// CHART REGISTRY — module-level, outside React
// Allows charts to directly call each other for crosshair sync
// Key: gridIndex, Value: { chart, getMainSeries }
// ============================================================
const chartRegistry = {};

function registerChart(index, chart, getMainSeries, getSeriesDataCache) {
  chartRegistry[index] = {
    chart,
    getMainSeries,
    getSeriesDataCache,
    isSyncing:          false,  // blocks range sync echo
    isCrosshairSyncing: false,  // blocks crosshair sync echo
    rangeThrottleTimer: null,   // throttle for range sync (prevents shivering)
  };
}

function unregisterChart(index) {
  delete chartRegistry[index];
}

function syncCrosshairToOthers(sourceIndex, time) {
  Object.entries(chartRegistry).forEach(([idx, entry]) => {
    if (parseInt(idx) === sourceIndex) return;
    const series = entry.getMainSeries();
    if (!series) return;

    if (time === null) {
      entry.chart.clearCrosshairPosition();
      return;
    }

    const ts = entry.chart.timeScale();
    const x  = ts.timeToCoordinate(time);

    if (x === null) {
      // Time outside visible range — scroll to it
      // Use isSyncing to block the echo from subscribeVisibleLogicalRangeChange
      const cacheData = entry.getSeriesDataCache
        ? entry.getSeriesDataCache()
        : null;
      const range = ts.getVisibleLogicalRange();
      if (cacheData && cacheData.length > 0 && range) {
        // Binary search nearest bar index
        let lo = 0, hi = cacheData.length - 1, nearIdx = 0;
        while (lo <= hi) {
          const mid = Math.floor((lo + hi) / 2);
          if (cacheData[mid].time <= time) { nearIdx = mid; lo = mid + 1; }
          else hi = mid - 1;
        }
        const half = (range.to - range.from) / 2;
        entry.isSyncing = true;
        ts.setVisibleLogicalRange({ from: nearIdx - half, to: nearIdx + half });
        requestAnimationFrame(() => {
          entry.isSyncing = false;
          entry.isCrosshairSyncing = true;
          entry.chart.setCrosshairPosition(Infinity, time, series);
          requestAnimationFrame(() => { entry.isCrosshairSyncing = false; });
        });
        return;
      }
    }

    // Time is visible — set crosshair directly
    entry.isCrosshairSyncing = true;
    entry.chart.setCrosshairPosition(Infinity, time, series);
    requestAnimationFrame(() => { entry.isCrosshairSyncing = false; });
  });
}

function syncRangeToOthers(sourceIndex, logicalRange) {
  // This function is now only called when the source chart scrolls
  // to a position where the center time would be OUTSIDE a target chart's
  // visible range. We check this before touching the target's range.
  if (!logicalRange) return;
  const sourceEntry = chartRegistry[sourceIndex];
  if (!sourceEntry) return;

  const sourceTS = sourceEntry.chart.timeScale();

  // Get center time of source chart's visible range
  const centerLogical = (logicalRange.from + logicalRange.to) / 2;
  const centerX       = sourceTS.logicalToCoordinate(centerLogical);
  const centerTime    = sourceTS.coordinateToTime(centerX);
  if (!centerTime) return;

  Object.entries(chartRegistry).forEach(([idx, entry]) => {
    if (parseInt(idx) === sourceIndex) return;
    if (entry.isSyncing) return;

    const ts    = entry.chart.timeScale();
    const range = ts.getVisibleLogicalRange();
    if (!range) return;

    // Check if centerTime is already visible on this chart
    const x = ts.timeToCoordinate(centerTime);
    if (x !== null) {
      // centerTime IS visible — do not touch range, crosshair sync handles it
      return;
    }

    // centerTime is NOT visible — scroll this chart to show it
    const centerL = ts.coordinateToLogical(x ?? ts.width() / 2);

    // Since x is null (out of range), find via cache binary search
    const cacheData = entry.getSeriesDataCache
      ? entry.getSeriesDataCache()
      : null;
    if (!cacheData || cacheData.length === 0) return;

    let lo = 0, hi = cacheData.length - 1, nearIdx = 0;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (cacheData[mid].time <= centerTime) { nearIdx = mid; lo = mid + 1; }
      else hi = mid - 1;
    }

    const half = (range.to - range.from) / 2;
    entry.isSyncing = true;
    ts.setVisibleLogicalRange({
      from: nearIdx - half,
      to:   nearIdx + half,
    });
    requestAnimationFrame(() => { entry.isSyncing = false; });
  });
}

// ============================================================
// CONSTANTS
// ============================================================
const TIMEFRAMES     = ['2min', '5min', '15min', '1H', '1D', '1W'];
const EMA_PERIODS    = ['ema_5', 'ema_9', 'ema_20', 'ema_50', 'ema_200'];
const INITIAL_LIMIT  = 300;
const LAZY_BATCH     = 300;
const LOAD_THRESHOLD = 20;

// Intraday timeframes — show time on X axis
const INTRADAY_TF = ['2min', '5min', '15min', '1H'];

const EMA_COLORS = {
  ema_5:   '#2962ff',
  ema_9:   '#ff6d00',
  ema_20:  '#2e7d32',
  ema_50:  '#6a1b9a',
  ema_200: '#b71c1c',
};

const LINE_SOURCE_COLORS = {
  close: '#131722',
  high:  '#26a69a',
  low:   '#ef5350',
};

const EMA_SOURCE_OPTIONS = ['close', 'high', 'low'];

// ============================================================
// TIMEZONE UTILITY
// ============================================================
// All timestamps from backend are IST (Asia/Kolkata, UTC+5:30).
// Lightweight Charts treats unix timestamps as UTC and labels
// the X axis in UTC — causing off-by-one-day for IST daily candles.
//
// Fix: convert the IST timestamp string to the IST wall-clock time,
// then express that as a UTC unix value. This way the chart always
// displays the correct IST date/time regardless of browser timezone.
//
// Example: "2026-05-08T18:30:00+00:00" (= 2026-05-09 00:00 IST)
//   → IST wall clock: 2026-05-09 00:00
//   → treated as UTC unix for that date: 2026-05-09T00:00:00Z
//   → chart labels show "May 9" ✓
//
// For intraday: "2026-05-09T03:45:00+00:00" (= 2026-05-09 09:15 IST)
//   → IST wall clock: 2026-05-09 09:15
//   → treated as UTC unix: 2026-05-09T09:15:00Z
//   → chart labels show "09:15" ✓

function toChartTime(isoString) {
  // Parse the ISO string to a Date object
  const d = new Date(isoString);

  // Convert to IST by adding 5h30m offset
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const istMs = d.getTime() + IST_OFFSET_MS;

  // Build a new Date that has IST wall-clock values in UTC fields
  // i.e. treat the IST time AS IF it were UTC
  const istWall = new Date(istMs);

  // Return unix seconds — chart will render these as UTC
  // which visually matches IST wall clock
  return Math.floor(istWall.getTime() / 1000);
}

// ============================================================
// INDICATORS MODAL
// ============================================================
function IndicatorsModal({
  settings,
  onToggleEMA, onSetEMASource,
  onSetEMAStyle, onSetLineStyle,
  onClose,
}) {
  const LINE_SOURCES = ['close', 'high', 'low'];
  const WIDTHS = [1, 2, 3];

  return (
    <div style={modal.overlay} onClick={onClose}>
      <div style={modal.box} onClick={(e) => e.stopPropagation()}>
        <div style={modal.header}>
          <span style={modal.title}>Indicators</span>
          <button style={modal.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ── EMA PERIODS ── */}
        <div style={modal.section}>
          <div style={modal.sectionLabel}>EMA Periods</div>
          {EMA_PERIODS.map((ema) => {
            const style = settings.emaStyles[ema];
            return (
              <div key={ema} style={modal.styleRow}>
                {/* Checkbox + label */}
                <label style={modal.checkRowInline}>
                  <input
                    type="checkbox"
                    checked={!!settings.visibleEMAs[ema]}
                    onChange={() => onToggleEMA(ema)}
                    style={{ marginRight: 6, accentColor: style.color }}
                  />
                  <span style={{ ...modal.colorDot, background: style.color }} />
                  <span style={modal.checkLabel}>
                    {ema.replace('ema_', 'EMA ')}
                  </span>
                </label>
                {/* Color picker */}
                <input
                  type="color"
                  value={style.color}
                  onChange={(e) => onSetEMAStyle(ema, 'color', e.target.value)}
                  style={modal.colorPicker}
                  title="Line color"
                />
                {/* Width buttons */}
                <div style={modal.widthBtns}>
                  {WIDTHS.map((w) => (
                    <button
                      key={w}
                      onClick={() => onSetEMAStyle(ema, 'width', w)}
                      style={{
                        ...modal.widthBtn,
                        ...(style.width === w ? modal.widthBtnActive : {}),
                      }}
                      title={`${w}px`}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── EMA SOURCE ── */}
        <div style={modal.section}>
          <div style={modal.sectionLabel}>Apply EMA on</div>
          <div style={modal.radioRow}>
            {EMA_SOURCE_OPTIONS.map((src) => (
              <label key={src} style={modal.radioLabel}>
                <input
                  type="radio"
                  name="emaSource"
                  value={src}
                  checked={settings.emaSource === src}
                  onChange={() => onSetEMASource(src)}
                  style={{ marginRight: 4 }}
                />
                {src.charAt(0).toUpperCase() + src.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* ── LINE STYLE — only shown when chart type is line ── */}
        {settings.chartType === 'line' && (
          <div style={modal.section}>
            <div style={modal.sectionLabel}>Line Style</div>
            {LINE_SOURCES.map((src) => {
              const style = settings.lineStyles[src];
              return (
                <div key={src} style={modal.styleRow}>
                  <span style={modal.checkLabel}>
                    {src.charAt(0).toUpperCase() + src.slice(1)}
                  </span>
                  {/* Color picker */}
                  <input
                    type="color"
                    value={style.color}
                    onChange={(e) => onSetLineStyle(src, 'color', e.target.value)}
                    style={modal.colorPicker}
                    title="Line color"
                  />
                  {/* Width buttons */}
                  <div style={modal.widthBtns}>
                    {WIDTHS.map((w) => (
                      <button
                        key={w}
                        onClick={() => onSetLineStyle(src, 'width', w)}
                        style={{
                          ...modal.widthBtn,
                          ...(style.width === w ? modal.widthBtnActive : {}),
                        }}
                        title={`${w}px`}
                      >
                        {w}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

// ============================================================
// CHART COMPONENT
// ============================================================
function Chart({ gridIndex = 0, isGrid = false, markers = [] }) {
  const chartContainerRef = useRef(null);
  const chartRef          = useRef(null);
  const seriesRefs        = useRef({});
  const resizeObserverRef = useRef(null);

  // Request ID — incremented on every full load.
  // Each async operation checks its captured ID against current.
  // If mismatch: a newer load started, abort this one.
  const loadIdRef          = useRef(0);
  const isLoadingMoreRef      = useRef(false);
  const isLoadingMoreRightRef = useRef(false);
  const hasMoreDataRef        = useRef(true);
  const hasMoreRightRef       = useRef(true);
  const oldestBucketRef       = useRef(null);
  const newestBucketRef       = useRef(null);
  const currentSymbolRef      = useRef(null);
  const tradesRef             = useRef([]);
  const vPatternsRef          = useRef([]);

  const {
    selectedSymbol,
    chartSettings,
    setChartSetting,
    toggleChartEMA,
    showMarkers,
    jumpTrigger,
    showVPatterns,
  } = useStore();

  const settings = chartSettings[gridIndex];
  const { timeframe, chartType, lineSources, emaSource, visibleEMAs } = settings;

  const [loading,   setLoading]   = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error,     setError]     = useState(null);
  const [legend,    setLegend]    = useState(null);
  const [jumpDate,  setJumpDate]  = useState('');

  // ── shortcuts ──
  const setTimeframe     = (tf)  => setChartSetting(gridIndex, 'timeframe', tf);
  const setChartType     = (ct)  => setChartSetting(gridIndex, 'chartType', ct);
  const setEMASource  = (src) => setChartSetting(gridIndex, 'emaSource', src);
  const toggleEMA     = (ema) => toggleChartEMA(gridIndex, ema);

  // Update style for a single EMA period
  const setEMAStyle = (ema, prop, val) => {
    const current = useStore.getState().chartSettings[gridIndex].emaStyles;
    setChartSetting(gridIndex, 'emaStyles', {
      ...current,
      [ema]: { ...current[ema], [prop]: val },
    });
  };

  // Update style for a line source (close/high/low)
  const setLineStyle = (src, prop, val) => {
    const current = useStore.getState().chartSettings[gridIndex].lineStyles;
    setChartSetting(gridIndex, 'lineStyles', {
      ...current,
      [src]: { ...current[src], [prop]: val },
    });
  };
  const toggleLineSource = (src) => {
    const cur = lineSources.includes(src)
      ? lineSources.filter((s) => s !== src)
      : [...lineSources, src];
    if (cur.length === 0) return;
    setChartSetting(gridIndex, 'lineSources', cur);
  };

  // ============================================================
  // INIT CHART — once on mount
  // ============================================================
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const isIntraday = INTRADAY_TF.includes(
      useStore.getState().chartSettings[gridIndex].timeframe
    );

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#FFFFFF' },
        textColor:  '#191919',
      },
      grid: {
        vertLines: { color: '#E0E3EB' },
        horzLines: { color: '#E0E3EB' },
      },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#D1D4DC' },
      timeScale: {
        borderColor:    '#D1D4DC',
        timeVisible:    isIntraday,
        secondsVisible: false,
      },
      width:  chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    chartRef.current = chart;

    // Resize
    resizeObserverRef.current = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) return;
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width:  chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
          });
        }
      });
    });
    resizeObserverRef.current.observe(chartContainerRef.current);

    // Lazy load on scroll — left and right
    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range) return;
      // Left edge — load older candles
      if (range.from < LOAD_THRESHOLD) loadMoreCandles();
      // Right edge — load newer candles when view reaches end of cache
      const cache = seriesRefs.current['_candleData']
        || seriesRefs.current['_lineData_close']
        || [];
      if (cache.length > 0 && range.to > cache.length - LOAD_THRESHOLD) {
        loadMoreCandlesRight();
      }
    });

    // Crosshair legend + sync broadcast (grid only)
    chart.subscribeCrosshairMove((param) => {
      if (!param || !param.time || !param.seriesData) {
        setLegend(null);

        return;
      }
      const refs = seriesRefs.current;
      let ohlc = null;
      if (refs['candle']) {
        const d = param.seriesData.get(refs['candle']);
        if (d) ohlc = { o: d.open, h: d.high, l: d.low, c: d.close };
      }
      let lineVal = null;
      ['close', 'high', 'low'].forEach((src) => {
        const s = refs[`line_${src}`];
        if (s) {
          const d = param.seriesData.get(s);
          if (d) lineVal = { ...lineVal, [src]: d.value };
        }
      });
      const emas = {};
      EMA_PERIODS.forEach((ema) => {
        const s = refs[ema];
        if (s) {
          const d = param.seriesData.get(s);
          if (d) emas[ema] = d.value;
        }
      });
      setLegend({ ohlc, lineVal, emas });
    });

    return () => {
      resizeObserverRef.current?.disconnect();
      chart.remove();
      chartRef.current   = null;
      seriesRefs.current = {};
    };
  }, []);

  // Update timeScale timeVisible when timeframe changes
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      timeScale: {
        timeVisible:    INTRADAY_TF.includes(timeframe),
        secondsVisible: false,
      },
    });
  }, [timeframe]);

  // ============================================================
  // REMOVE ALL SERIES — full wipe including caches
  // ============================================================
  const removeAllSeries = () => {
    if (!chartRef.current) return;
    Object.entries(seriesRefs.current).forEach(([key, val]) => {
      if (key.startsWith('_')) return;
      try { chartRef.current.removeSeries(val); } catch (_) {}
    });
    seriesRefs.current = {};
};

  // ============================================================
  // LOAD DATA — triggered by symbol, timeframe, chartType change
  // ============================================================
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    loadChartData();
  }, [selectedSymbol, timeframe, chartType]);

  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    if (chartType !== 'line') return;
    loadChartData();
  }, [lineSources]);

  // EMA settings change — redraw EMAs only
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    redrawEMAs();
  }, [visibleEMAs, emaSource]);

  // V Patterns toggle — show/hide V pattern markers
  const isMountedVPRef = useRef(false);
  useEffect(() => {
    if (!isMountedVPRef.current) { isMountedVPRef.current = true; return; }
    if (!chartRef.current || !selectedSymbol) return;
    const mainSeries = seriesRefs.current['candle']
      || seriesRefs.current['line_close']
      || seriesRefs.current['line_high']
      || seriesRefs.current['line_low'];
    if (!mainSeries) return;
    if (showVPatterns && vPatternsRef.current && vPatternsRef.current.length > 0) {
      const cache = seriesRefs.current['_candleData']
        || seriesRefs.current['_lineData_close'];
      const allMarkers = buildVPatternMarkers(vPatternsRef.current);
      const filtered   = cache && cache.length > 0
        ? allMarkers.filter(m =>
            m.time >= cache[0].time && m.time <= cache[cache.length - 1].time)
        : allMarkers;
      applyMarkersToSeries(mainSeries, filtered);
    } else if (!showVPatterns) {
      // Only clear V pattern markers — re-apply trade markers if needed
      if (showMarkers && tradesRef.current.length > 0) {
        const cache = seriesRefs.current['_candleData']
          || seriesRefs.current['_lineData_close'];
        const allMarkers = buildMarkersFromTrades(tradesRef.current);
        const filtered   = cache && cache.length > 0
          ? allMarkers.filter(m =>
              m.time >= cache[0].time && m.time <= cache[cache.length - 1].time)
          : allMarkers;
        applyMarkersToSeries(mainSeries, filtered);
      } else {
        removeMarkersFromSeries(mainSeries);
      }
    }
  }, [showVPatterns]);

  // Jump trigger — fires when TradesView selects a trade
  // All active charts jump to the triggered date simultaneously
  useEffect(() => {
    if (!jumpTrigger || !jumpTrigger.date) return;
    if (!chartRef.current) return;
    // Ensure date is a plain string (YYYY-MM-DD)
    const dateStr = String(jumpTrigger.date).slice(0, 10);
    setJumpDate(dateStr);
    setTimeout(() => jumpToDate(dateStr), 50);
  }, [jumpTrigger]);

  // Live candle polling — updates open candle every 3s during market hours
  // Only for intraday timeframes when WebSocket is running
  useEffect(() => {
    if (!INTRADAY_TF.includes(timeframe)) return;
    if (!selectedSymbol) return;

    const poll = async () => {
      if (!chartRef.current) return;
      try {
        const data = await fetchLiveCandle(selectedSymbol, timeframe);
        if (!data || !data.candle) return;

        const c = data.candle;
        // Convert bucket IST time to chart time
        const IST_OFFSET = 5.5 * 60 * 60 * 1000;
        const d          = new Date(c.bucket);
        const istMs      = d.getTime() + IST_OFFSET;
        const chartTime  = Math.floor(new Date(istMs).getTime() / 1000);

        const series = seriesRefs.current['candle']
          || seriesRefs.current['line_close']
          || seriesRefs.current['line_high']
          || seriesRefs.current['line_low'];
        if (!series) return;

        if (seriesRefs.current['candle']) {
          // Candlestick update
          series.update({
            time:  chartTime,
            open:  parseFloat(c.open),
            high:  parseFloat(c.high),
            low:   parseFloat(c.low),
            close: parseFloat(c.close),
          });
        } else {
          // Line chart update — use close
          series.update({
            time:  chartTime,
            value: parseFloat(c.close),
          });
        }
      } catch (_) {
        // Silent fail — WebSocket may not be running
      }
    };

    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [selectedSymbol, timeframe]);

  // Markers toggle — show/hide markers without reloading data
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    const mainSeries = seriesRefs.current['candle']
      || seriesRefs.current['line_close']
      || seriesRefs.current['line_high']
      || seriesRefs.current['line_low'];
    if (!mainSeries) return;
    if (showMarkers && tradesRef.current && tradesRef.current.length > 0) {
      const cache = seriesRefs.current['_candleData']
        || seriesRefs.current['_lineData_close'];
      const allMarkers = buildMarkersFromTrades(tradesRef.current);
      const filteredMarkers = cache && cache.length > 0
        ? allMarkers.filter(m =>
            m.time >= cache[0].time &&
            m.time <= cache[cache.length - 1].time
          )
        : allMarkers;
      applyMarkersToSeries(mainSeries, filteredMarkers);
    } else {
      removeMarkersFromSeries(mainSeries);
    }
  }, [showMarkers]);

  // EMA style change — update color/width on existing series instantly
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    const emaStyles = useStore.getState().chartSettings[gridIndex].emaStyles;
    EMA_PERIODS.forEach((ema) => {
      const series = seriesRefs.current[ema];
      if (!series) return;
      const s = emaStyles[ema];
      series.applyOptions({ color: s.color, lineWidth: s.width });
    });
  }, [settings.emaStyles]);

  // Line style change — update color/width on existing series instantly
  useEffect(() => {
    if (!chartRef.current || !selectedSymbol) return;
    const lineStyles = useStore.getState().chartSettings[gridIndex].lineStyles;
    ['close', 'high', 'low'].forEach((src) => {
      const series = seriesRefs.current[`line_${src}`];
      if (!series) return;
      const s = lineStyles[src];
      series.applyOptions({ color: s.color, lineWidth: s.width });
    });
  }, [settings.lineStyles]);

  // ============================================================
  // CROSSHAIR SYNC — apply incoming sync from other grid charts
  // ============================================================
  // ============================================================
  // LOAD CHART DATA — full reload with race condition protection
  // ============================================================
  const loadChartData = async () => {
    if (!chartRef.current || !selectedSymbol) return;

    // Increment load ID — any previous async load will see mismatch and abort
    const myLoadId = ++loadIdRef.current;

    // debug started
    const symbol = selectedSymbol;
    const tf     = useStore.getState().chartSettings[gridIndex].timeframe;
    const ct     = useStore.getState().chartSettings[gridIndex].chartType;
    const ls     = useStore.getState().chartSettings[gridIndex].lineSources;

    
    // Reset lazy load state immediately (sync)
    oldestBucketRef.current       = null;
    newestBucketRef.current       = null;
    isLoadingMoreRef.current      = false;
    isLoadingMoreRightRef.current = false;
    hasMoreDataRef.current        = true;
    hasMoreRightRef.current       = true;
    currentSymbolRef.current      = selectedSymbol;

    setLoading(true);
    setError(null);
    setLegend(null);

    // Capture values at start of this load — don't read from closure later
    
    
    try {
      const candles = await fetchCandles(symbol, tf, INITIAL_LIMIT);

      // Abort if a newer load started while we were fetching
      if (myLoadId !== loadIdRef.current) return;

      // Now safe to wipe and redraw
      removeAllSeries();

      if (!candles || candles.length === 0) {
        setError('No data');
        setLoading(false);
        return;
      }

      oldestBucketRef.current  = candles[0].bucket;
      newestBucketRef.current  = candles[candles.length - 1].bucket;
      currentSymbolRef.current = symbol;

      if (ct === 'candlestick') {
        drawCandlestick(candles);
      } else {
        drawLine(candles, ls);
      }

      // Check again before drawing EMAs
      if (myLoadId !== loadIdRef.current) return;

      await redrawEMAsForLoad(symbol, tf, myLoadId);

      if (myLoadId !== loadIdRef.current) return;

      const total = candles.length;
      chartRef.current?.timeScale().setVisibleLogicalRange({
        from: Math.max(0, total - 120),
        to:   total + 2,
      });

      // Fetch and apply trade markers — non-fatal if fails
      try {
        const trades = await fetchTradesForChart(symbol);
        if (myLoadId !== loadIdRef.current) return;
        tradesRef.current = trades || [];
        const shouldShow = useStore.getState().showMarkers;
        if (shouldShow && trades && trades.length > 0) {
          const mainSeries = seriesRefs.current['candle']
            || seriesRefs.current['line_close']
            || seriesRefs.current['line_high']
            || seriesRefs.current['line_low'];
          if (mainSeries) {
            // Only show markers within loaded candle range
            const cache = seriesRefs.current['_candleData']
              || seriesRefs.current['_lineData_close'];
            const allMarkers = buildMarkersFromTrades(trades);
            const filteredMarkers = cache && cache.length > 0
              ? allMarkers.filter(m =>
                  m.time >= cache[0].time &&
                  m.time <= cache[cache.length - 1].time
                )
              : allMarkers;
            if (filteredMarkers.length > 0) {
              await applyMarkersToSeries(mainSeries, filteredMarkers);
            }
          }
        }
      } catch (markerErr) {
        console.warn('Trade markers load error:', markerErr);
      }

      // Fetch and apply V pattern markers (only when showVPatterns=true)
      try {
        const shouldShowV = useStore.getState().showVPatterns;
        if (shouldShowV) {
          const vpFilters = useStore.getState().vpFilters;
          const filters   = {};
          if (vpFilters.ema)            filters.ema           = vpFilters.ema;
          if (vpFilters.fromDate)       filters.from_date     = vpFilters.fromDate;
          if (vpFilters.toDate)         filters.to_date       = vpFilters.toDate;
          if (vpFilters.direction)      filters.direction     = vpFilters.direction;
          if (vpFilters.max_gap    != null) filters.max_gap      = vpFilters.max_gap;
          if (vpFilters.min_inter_gap != null) filters.min_inter_gap = vpFilters.min_inter_gap;
          if (vpFilters.trend_confirmed != null) filters.trend_confirmed = vpFilters.trend_confirmed;
          if (vpFilters.close_rising    != null) filters.close_rising    = vpFilters.close_rising;
          if (vpFilters.ema_rising      != null) filters.ema_rising      = vpFilters.ema_rising;
          const vPatterns = await fetchVPatternsForChart(symbol, timeframe, filters);
          if (myLoadId !== loadIdRef.current) return;
          vPatternsRef.current = vPatterns || [];
          if (vPatterns && vPatterns.length > 0) {
            const mainSeries = seriesRefs.current['candle']
              || seriesRefs.current['line_close']
              || seriesRefs.current['line_high']
              || seriesRefs.current['line_low'];
            if (mainSeries) {
              const cache = seriesRefs.current['_candleData']
                || seriesRefs.current['_lineData_close'];
              const allMarkers = buildVPatternMarkers(vPatterns);
              const filtered   = cache && cache.length > 0
                ? allMarkers.filter(m =>
                    m.time >= cache[0].time && m.time <= cache[cache.length - 1].time)
                : allMarkers;
              if (filtered.length > 0) {
                await applyMarkersToSeries(mainSeries, filtered);
              }
            }
          }
        }
      } catch (vpErr) {
        console.warn('V pattern markers load error:', vpErr);
      }

    } catch (err) {
      if (myLoadId !== loadIdRef.current) return;
      console.error('Chart load error:', err);
      setError('Failed to load data');
    }

    if (myLoadId === loadIdRef.current) setLoading(false);
  };

  // ============================================================
  // LAZY LOAD — fetch older candles on scroll
  // ============================================================
  const loadMoreCandles = async () => {
    if (!chartRef.current || !selectedSymbol)  return;
    if (isLoadingMoreRef.current)              return;
    if (!hasMoreDataRef.current)               return;
    if (!oldestBucketRef.current)              return;

    // Only lazy-load for current symbol — guard against stale closures
    const symbol = useStore.getState().selectedSymbol;
    const s      = useStore.getState().chartSettings[gridIndex];
    const tf     = s.timeframe;

    isLoadingMoreRef.current = true;

    try {
      const toTime = new Date(
        new Date(oldestBucketRef.current).getTime() - 1000
      ).toISOString();

      const candles = await fetchCandles(symbol, tf, LAZY_BATCH, toTime);
      if (!candles || candles.length === 0) {
        hasMoreDataRef.current = false;
        isLoadingMoreRef.current = false;
        return;
      }
      if (candles.length < LAZY_BATCH) hasMoreDataRef.current = false;
      oldestBucketRef.current = candles[0].bucket;
      // Set newestBucketRef if not yet set (first load after jump)
      if (!newestBucketRef.current) {
        newestBucketRef.current = candles[candles.length - 1].bucket;
      }

      const oldIndicators = await fetchIndicators(symbol, tf, LAZY_BATCH, toTime);

      prependCandles(candles, symbol, tf);
      if (oldIndicators && oldIndicators.length > 0) {
        prependIndicators(oldIndicators, s.emaSource);
      }

    } catch (err) {
      console.error('Lazy load error:', err);
    }

    isLoadingMoreRef.current = false;
  };

  // ============================================================
  // APPEND INDICATORS — right side (newer data)
  // Mirror of prependIndicators
  // ============================================================
  const appendIndicators = (newIndicators, srcOverride) => {
    const src = srcOverride
      || useStore.getState().chartSettings[gridIndex].emaSource;

    EMA_PERIODS.forEach((emaKey) => {
      if (!seriesRefs.current[emaKey]) return; // EMA not active
      const colName = `${emaKey}_${src}`;
      const newData = newIndicators
        .filter((i) => i[colName] != null)
        .map((i) => ({ time: toChartTime(i.time), value: parseFloat(i[colName]) }));
      if (newData.length === 0) return;
      const cacheKey = `_emaData_${emaKey}`;
      const merged   = [...(seriesRefs.current[cacheKey] || []), ...newData];
      seriesRefs.current[emaKey].setData(merged);
      seriesRefs.current[cacheKey] = merged;
    });
  };

  // ============================================================
  // DRAW CANDLESTICK
  // ============================================================
  const drawCandlestick = (candles) => {
    
    const series = chartRef.current.addSeries(CandlestickSeries, {
      upColor:       '#26a69a',
      downColor:     '#ef5350',
      borderVisible: false,
      wickUpColor:   '#26a69a',
      wickDownColor: '#ef5350',
    });
    const data = candles.map((c) => ({
      time:  toChartTime(c.bucket),
      open:  parseFloat(c.open),
      high:  parseFloat(c.high),
      low:   parseFloat(c.low),
      close: parseFloat(c.close),
    }));
    series.setData(data);
    seriesRefs.current['candle']      = series;
    seriesRefs.current['_candleData'] = data;
  };

  // ============================================================
  // DRAW LINE(S)
  // ============================================================
  const drawLine = (candles, ls) => {
    const sources    = ls || lineSources;
    const lineStyles = useStore.getState().chartSettings[gridIndex].lineStyles;
    sources.forEach((src) => {
      const style  = lineStyles[src];
      const series = chartRef.current.addSeries(LineSeries, {
        color:            style.color,
        lineWidth:        style.width,
        priceLineVisible: false,
      });
      const data = candles.map((c) => ({
        time:  toChartTime(c.bucket),
        value: parseFloat(c[src]),
      }));
      series.setData(data);
      seriesRefs.current[`line_${src}`]      = series;
      seriesRefs.current[`_lineData_${src}`] = data;
    });
  };

  // ============================================================
  // LOAD MORE CANDLES — RIGHT (newer data)
  // Exact mirror of loadMoreCandles but fetches forward in time
  // ============================================================
  const loadMoreCandlesRight = async () => {
    if (!chartRef.current || !selectedSymbol)   return;
    if (isLoadingMoreRightRef.current)           return;
    if (!hasMoreRightRef.current)                return;
    if (!newestBucketRef.current)                return;

    const sym = useStore.getState().selectedSymbol;
    const s   = useStore.getState().chartSettings[gridIndex];
    const tf  = s.timeframe;

    isLoadingMoreRightRef.current = true;

    try {
      // from_time = one second after newest loaded candle
      // fetch next LAZY_BATCH candles after that
      const fromTime = new Date(
        new Date(newestBucketRef.current).getTime() + 1000
      ).toISOString();

      // Use fetchCandlesAfter — from_time + limit, no to_time
      const candles = await fetchCandlesAfter(sym, tf, fromTime, LAZY_BATCH);

      if (!candles || candles.length === 0) {
        // No more data — at the latest candle
        hasMoreRightRef.current = false;
        isLoadingMoreRightRef.current = false;
        return;
      }

      // If fewer candles than batch, we've reached the latest data
      if (candles.length < LAZY_BATCH) {
        hasMoreRightRef.current = false;
      }

      // Update newest bucket to latest fetched candle
      newestBucketRef.current = candles[candles.length - 1].bucket;

      // Fetch matching indicators for this batch
      const newIndicators = await fetchIndicators(sym, tf, LAZY_BATCH, null, fromTime);

      // Append candles and indicators
      appendCandles(candles);
      if (newIndicators && newIndicators.length > 0) {
        appendIndicators(newIndicators, s.emaSource);
      }

    } catch (err) {
      console.error('Right lazy load error:', err);
    }

    isLoadingMoreRightRef.current = false;
  };

  // ============================================================
  // PREPEND CANDLES
  // ============================================================
  const prependCandles = (newCandles) => {
    const s  = useStore.getState().chartSettings[gridIndex];
    const ct = s.chartType;
    const ls = s.lineSources;

    if (ct === 'candlestick' && seriesRefs.current['candle']) {
      const newData = newCandles.map((c) => ({
        time:  toChartTime(c.bucket),
        open:  parseFloat(c.open),
        high:  parseFloat(c.high),
        low:   parseFloat(c.low),
        close: parseFloat(c.close),
      }));
      const merged = [...newData, ...(seriesRefs.current['_candleData'] || [])];
      seriesRefs.current['candle'].setData(merged);
      seriesRefs.current['_candleData'] = merged;
    } else if (ct === 'line') {
      ls.forEach((src) => {
        const key = `line_${src}`;
        if (!seriesRefs.current[key]) return;
        const newData = newCandles.map((c) => ({
          time:  toChartTime(c.bucket),
          value: parseFloat(c[src]),
        }));
        const cacheKey = `_lineData_${src}`;
        const merged   = [...newData, ...(seriesRefs.current[cacheKey] || [])];
        seriesRefs.current[key].setData(merged);
        seriesRefs.current[cacheKey] = merged;
      });
    }
  };

  // ============================================================
  // APPEND CANDLES — right side (newer data)
  // ============================================================
  const appendCandles = (newCandles) => {
    const s  = useStore.getState().chartSettings[gridIndex];
    const ct = s.chartType;
    const ls = s.lineSources;

    if (ct === 'candlestick' && seriesRefs.current['candle']) {
      const newData = newCandles.map((c) => ({
        time:  toChartTime(c.bucket),
        open:  parseFloat(c.open),
        high:  parseFloat(c.high),
        low:   parseFloat(c.low),
        close: parseFloat(c.close),
      }));
      const merged = [...(seriesRefs.current['_candleData'] || []), ...newData];
      seriesRefs.current['candle'].setData(merged);
      seriesRefs.current['_candleData'] = merged;
    } else if (ct === 'line') {
      ls.forEach((src) => {
        const key = `line_${src}`;
        if (!seriesRefs.current[key]) return;
        const newData = newCandles.map((c) => ({
          time:  toChartTime(c.bucket),
          value: parseFloat(c[src]),
        }));
        const cacheKey = `_lineData_${src}`;
        const merged   = [...(seriesRefs.current[cacheKey] || []), ...newData];
        seriesRefs.current[key].setData(merged);
        seriesRefs.current[cacheKey] = merged;
      });
    }
  };

  // ============================================================
  // PREPEND INDICATORS
  // ============================================================
  const prependIndicators = (newIndicators, srcOverride) => {
    const src = srcOverride
      || useStore.getState().chartSettings[gridIndex].emaSource;

    EMA_PERIODS.forEach((emaKey) => {
      if (!seriesRefs.current[emaKey]) return;
      const colName = `${emaKey}_${src}`;
      const newData = newIndicators
        .filter((i) => i[colName] != null)
        .map((i) => ({ time: toChartTime(i.time), value: parseFloat(i[colName]) }));
      if (newData.length === 0) return;
      const cacheKey = `_emaData_${emaKey}`;
      const merged   = [...newData, ...(seriesRefs.current[cacheKey] || [])];
      seriesRefs.current[emaKey].setData(merged);
      seriesRefs.current[cacheKey] = merged;
    });
  };

  // ============================================================
  // REDRAW EMAs — for initial load (called with explicit args)
  // Accepts loadId so it can abort if superseded
  // ============================================================
  const redrawEMAsForLoad = async (symbol, tf, myLoadId) => {
    if (!chartRef.current) return;
    

    const s          = useStore.getState().chartSettings[gridIndex];
    const freshEMAs  = s.visibleEMAs;
    const freshSrc   = s.emaSource;

    // Remove existing EMA series
    EMA_PERIODS.forEach((ema) => {
      if (seriesRefs.current[ema]) {
        try { chartRef.current.removeSeries(seriesRefs.current[ema]); } catch (_) {}
        delete seriesRefs.current[ema];
        delete seriesRefs.current[`_emaData_${ema}`];
      }
    });

    const activeEMAs = EMA_PERIODS.filter((e) => freshEMAs[e]);
    if (activeEMAs.length === 0) return;

    try {
      const indicators = await fetchIndicators(symbol, tf, INITIAL_LIMIT);
      
      if (myLoadId !== loadIdRef.current) return; // abort if superseded
      if (!indicators || indicators.length === 0) return;

      activeEMAs.forEach((emaKey) => {
        const colName = `${emaKey}_${freshSrc}`;
        const data = indicators
          .filter((i) => i[colName] != null)
          .map((i) => ({ time: toChartTime(i.time), value: parseFloat(i[colName]) }));
        if (data.length === 0) return;
        const emaStyle = useStore.getState().chartSettings[gridIndex].emaStyles[emaKey];
        const series = chartRef.current.addSeries(LineSeries, {
          color:            emaStyle.color,
          lineWidth:        emaStyle.width,
          priceLineVisible: false,
        });
        series.setData(data);
        seriesRefs.current[emaKey]               = series;
        seriesRefs.current[`_emaData_${emaKey}`] = data;
        seriesRefs.current[`_emaSrc_${emaKey}`]  = freshSrc;
      });
    } catch (err) {
      console.error('EMA load error (initial):', err);
    }
  };

  // ============================================================
  // REDRAW EMAs — called when user toggles EMA or changes source
  // Reads everything fresh from store
  // Fetches full range if lazy-loaded data is present
  // ============================================================
  const redrawEMAs = async () => {
    if (!chartRef.current) return;

    const currentLoadId = loadIdRef.current;
    const sym = useStore.getState().selectedSymbol;
    const s   = useStore.getState().chartSettings[gridIndex];
    const tf  = s.timeframe;
    
    

    const src = s.emaSource;
    const ems = s.visibleEMAs;

    if (!sym) return;

    // Step 1: Remove only EMAs that are now OFF
    // — do this immediately (sync) so unchecked lines disappear at once
    EMA_PERIODS.forEach((ema) => {
      if (!ems[ema] && seriesRefs.current[ema]) {
        try { chartRef.current.removeSeries(seriesRefs.current[ema]); } catch (_) {}
        delete seriesRefs.current[ema];
        delete seriesRefs.current[`_emaData_${ema}`];
      }
    });

    // Step 2: Find EMAs that need to be added or updated
    // — "added": checked ON but no series exists yet
    // — "source changed": series exists but was drawn with different src
    //   (we track source in _emaSource cache key)
    const toAdd = EMA_PERIODS.filter((e) => {
      if (!ems[e]) return false;               // not active
      if (!seriesRefs.current[e]) return true; // active but missing — add
      // active and exists — check if source changed
      if (seriesRefs.current[`_emaSrc_${e}`] !== src) return true;
      return false;
    });

    if (toAdd.length === 0) return; // nothing to fetch or draw

    try {
      let indicators;
      if (oldestBucketRef.current && newestBucketRef.current) {
        // Fetch indicators matching the currently loaded candle range only
        // Use from_time + to_time to avoid fetching years of data at once
        indicators = await fetchIndicatorsByRange(
          sym, tf,
          oldestBucketRef.current,
          newestBucketRef.current,
        );
      } else {
        indicators = await fetchIndicators(sym, tf, INITIAL_LIMIT);
      }

      // Abort if chart was reloaded while fetching
      if (loadIdRef.current !== currentLoadId) return;
      if (!chartRef.current) return;
      if (!indicators || indicators.length === 0) return;

      toAdd.forEach((emaKey) => {
        // Remove old series if source changed
        if (seriesRefs.current[emaKey]) {
          try { chartRef.current.removeSeries(seriesRefs.current[emaKey]); } catch (_) {}
          delete seriesRefs.current[emaKey];
          delete seriesRefs.current[`_emaData_${emaKey}`];
        }

        const colName = `${emaKey}_${src}`;
        const data = indicators
          .filter((i) => i[colName] != null)
          .map((i) => ({ time: toChartTime(i.time), value: parseFloat(i[colName]) }));
        if (data.length === 0) return;

        const emaStyle = useStore.getState().chartSettings[gridIndex].emaStyles[emaKey];
        const series = chartRef.current.addSeries(LineSeries, {
          color:            emaStyle.color,
          lineWidth:        emaStyle.width,
          priceLineVisible: false,
        });
        series.setData(data);
        seriesRefs.current[emaKey]               = series;
        seriesRefs.current[`_emaData_${emaKey}`] = data;
        seriesRefs.current[`_emaSrc_${emaKey}`]  = src;
      });
    } catch (err) {
      console.error('EMA load error:', err);
    }
  };

  // ============================================================
  // ============================================================
  // JUMP TO DATE
  // Loads a fresh window of INITIAL_LIMIT candles centered on
  // the target date. Lazy load works in both directions from there.
  // ============================================================
  const jumpToDate = async (overrideDate = null) => {
    const dateToUse = overrideDate || jumpDate;
    if (!chartRef.current || !dateToUse) return;

    const myLoadId = ++loadIdRef.current;
    const sym = useStore.getState().selectedSymbol;
    const s   = useStore.getState().chartSettings[gridIndex];
    const tf  = s.timeframe;
    const ct  = s.chartType;
    const ls  = s.lineSources;

    setLoading(true);
    setError(null);

    // Reset all lazy load state
    oldestBucketRef.current       = null;
    newestBucketRef.current       = null;
    isLoadingMoreRef.current      = false;
    isLoadingMoreRightRef.current = false;
    hasMoreDataRef.current        = true;
    hasMoreRightRef.current       = true;

    try {
      // Fetch INITIAL_LIMIT candles BEFORE target date
      const toTime   = `${dateToUse}T23:59:59+05:30`;
      const beforeCandles = await fetchCandles(sym, tf, INITIAL_LIMIT, toTime);
      if (myLoadId !== loadIdRef.current) return;

      // Fetch INITIAL_LIMIT candles AFTER target date (from start of day)
      const fromTime  = `${dateToUse}T00:00:00+05:30`;
      const afterCandles = await fetchCandlesAfter(sym, tf, fromTime, INITIAL_LIMIT);
      if (myLoadId !== loadIdRef.current) return;

      // Merge: before + after, deduplicate by time, sort ASC
      const allCandles = [...(beforeCandles || []), ...(afterCandles || [])];
      const seen = new Set();
      const candles = allCandles
        .filter((c) => {
          if (seen.has(c.bucket)) return false;
          seen.add(c.bucket);
          return true;
        })
        .sort((a, b) => new Date(a.bucket) - new Date(b.bucket));

      if (!candles || candles.length === 0) {
        setError('No data for this date');
        setLoading(false);
        return;
      }

      oldestBucketRef.current  = candles[0].bucket;
      newestBucketRef.current  = candles[candles.length - 1].bucket;

      removeAllSeries();
      
      if (ct === 'candlestick') drawCandlestick(candles);
      else drawLine(candles, ls);

      await redrawEMAs();
      if (myLoadId !== loadIdRef.current) return;

      // Fetch fresh trades for current symbol and apply markers
      try {
        const shouldShow = useStore.getState().showMarkers;
        const trades     = await fetchTradesForChart(sym);
        if (myLoadId !== loadIdRef.current) return;
        tradesRef.current = trades || [];
        if (shouldShow && trades && trades.length > 0) {
          const mainSeries = seriesRefs.current['candle']
            || seriesRefs.current['line_close']
            || seriesRefs.current['line_high']
            || seriesRefs.current['line_low'];
          if (mainSeries) {
            await applyMarkersToSeries(
              mainSeries,
              buildMarkersFromTrades(trades)
            );
          }
        }
      } catch (_) {}

      // Find FIRST candle of target date and center view on it
      const IST_OFFSET = 5.5 * 60 * 60 * 1000;
      // Start of target date in IST
      const targetStartTime = Math.floor(
        (new Date(`${dateToUse}T00:00:00+05:30`).getTime() + IST_OFFSET) / 1000
      );
      // End of target date in IST
      const targetEndTime = Math.floor(
        (new Date(`${dateToUse}T23:59:59+05:30`).getTime() + IST_OFFSET) / 1000
      );

      const cache = seriesRefs.current['_candleData']
        || seriesRefs.current['_lineData_close']
        || seriesRefs.current['_lineData_high']
        || seriesRefs.current['_lineData_low'];

      if (cache && cache.length > 0) {
        // Find first candle of target date (>= start of day)
        let firstIdx = cache.findIndex(
          (c) => c.time >= targetStartTime && c.time <= targetEndTime
        );
        // If no candle found for exact date (holiday/weekend), use nearest
        if (firstIdx === -1) {
          let lo = 0, hi = cache.length - 1;
          firstIdx = cache.length - 1;
          while (lo <= hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (cache[mid].time <= targetStartTime) {
              firstIdx = mid; lo = mid + 1;
            } else hi = mid - 1;
          }
        }
        // Center view on first candle of target date
        // Show 100 candles before and 100 after
        chartRef.current.timeScale().setVisibleLogicalRange({
          from: Math.max(0, firstIdx - 100),
          to:   Math.min(cache.length - 1, firstIdx + 100),
        });
      }

    } catch (err) {
      console.error('Jump to date error:', err);
      setError('Failed to jump to date');
    }

    if (myLoadId === loadIdRef.current) setLoading(false);
  };

    // ============================================================
  // ZOOM CONTROLS
  // ============================================================
  const zoomIn = () => {
    if (!chartRef.current) return;
    const ts = chartRef.current.timeScale();
    const r  = ts.getVisibleLogicalRange();
    if (!r) return;
    const c = (r.from + r.to) / 2;
    const h = (r.to - r.from) / 2 * 0.7;
    ts.setVisibleLogicalRange({ from: c - h, to: c + h });
  };

  const zoomOut = () => {
    if (!chartRef.current) return;
    const ts = chartRef.current.timeScale();
    const r  = ts.getVisibleLogicalRange();
    if (!r) return;
    const c = (r.from + r.to) / 2;
    const h = (r.to - r.from) / 2 * 1.4;
    ts.setVisibleLogicalRange({ from: c - h, to: c + h });
  };

  const zoomReset = () => {
    if (!chartRef.current) return;
    const total = (seriesRefs.current['_candleData'] || []).length || 120;
    chartRef.current.timeScale().setVisibleLogicalRange({
      from: Math.max(0, total - 120),
      to:   total + 2,
    });
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={styles.wrapper}>

      {/* ── TOOLBAR ── */}
      <div style={styles.toolbar}>
        <span style={styles.symbolLabel}>{selectedSymbol}</span>
        <span style={styles.divider} />

        {TIMEFRAMES.map((tf) => (
          <button key={tf}
            onClick={() => setTimeframe(tf)}
            style={tf === timeframe ? styles.btnActive : styles.btn}
          >{tf}</button>
        ))}

        <span style={styles.divider} />
        <button onClick={() => setChartType('candlestick')}
          style={chartType === 'candlestick' ? styles.btnActive : styles.btn}
          title="Candlestick">▮▮</button>
        <button onClick={() => setChartType('line')}
          style={chartType === 'line' ? styles.btnActive : styles.btn}
          title="Line">∿</button>

        {chartType === 'line' && (
          <>
            <span style={styles.divider} />
            {['close', 'high', 'low'].map((src) => (
              <button key={src} onClick={() => toggleLineSource(src)}
                style={{
                  ...(lineSources.includes(src) ? styles.btnActive : styles.btn),
                  color: lineSources.includes(src) ? '#fff' : LINE_SOURCE_COLORS[src],
                }}
              >{src.charAt(0).toUpperCase() + src.slice(1)}</button>
            ))}
          </>
        )}

        <span style={styles.divider} />
        <button onClick={() => setShowModal(true)} style={styles.btn}>
          Indicators
        </button>
        <span style={styles.divider} />
        <button onClick={zoomIn}    style={styles.btn} title="Zoom In">+</button>
        <button onClick={zoomOut}   style={styles.btn} title="Zoom Out">−</button>
        <button onClick={zoomReset} style={styles.btn} title="Reset Zoom">⊡</button>

        <span style={styles.divider} />

        {/* Jump to date */}
        <input
          type="date"
          value={jumpDate}
          onChange={(e) => setJumpDate(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && jumpToDate()}
          style={styles.dateInput}
          title="Jump to date"
        />
        <button
          onClick={() => jumpToDate()}
          style={styles.btn}
          title="Jump to date"
        >Go</button>

        {loading && <span style={styles.loading}>Loading…</span>}
        {error   && <span style={styles.errorText}>{error}</span>}
      </div>

      {/* ── CHART AREA ── */}
      <div style={styles.chartArea}>

        {/* Custom legend — top left */}
        {legend && (
          <div style={styles.legend}>
            {legend.ohlc && (
              <span style={styles.legendOHLC}>
                O <b>{fmt(legend.ohlc.o)}</b>&nbsp;
                H <b style={{color:'#26a69a'}}>{fmt(legend.ohlc.h)}</b>&nbsp;
                L <b style={{color:'#ef5350'}}>{fmt(legend.ohlc.l)}</b>&nbsp;
                C <b>{fmt(legend.ohlc.c)}</b>
              </span>
            )}
            {legend.lineVal && Object.entries(legend.lineVal).map(([src, val]) => (
              <span key={src} style={{color: settings.lineStyles[src].color, marginRight: 8}}>
                {src.charAt(0).toUpperCase() + src.slice(1)}: <b>{fmt(val)}</b>
              </span>
            ))}
            {Object.entries(legend.emas).map(([ema, val]) => (
              <span key={ema} style={{color: settings.emaStyles[ema].color, marginRight: 6}}>
                {ema.replace('ema_', 'E')}: <b>{fmt(val)}</b>
              </span>
            ))}
          </div>
        )}

        <div ref={chartContainerRef} style={styles.chartContainer} />
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <IndicatorsModal
          settings={settings}
          onToggleEMA={toggleEMA}
          onSetEMASource={setEMASource}
          onSetEMAStyle={setEMAStyle}
          onSetLineStyle={setLineStyle}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// FORMAT NUMBER
// ============================================================
function fmt(val) {
  if (val == null) return '-';
  return Number(val).toFixed(2);
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  wrapper: {
    display:         'flex',
    flexDirection:   'column',
    height:          '100%',
    width:           '100%',
    backgroundColor: '#ffffff',
    border:          '1px solid #e0e0e0',
    borderRadius:    '4px',
    overflow:        'hidden',
  },
  toolbar: {
    display:         'flex',
    alignItems:      'center',
    gap:             '3px',
    padding:         '4px 8px',
    backgroundColor: '#fafafa',
    borderBottom:    '1px solid #e0e0e0',
    flexWrap:        'wrap',
    flexShrink:      0,
  },
  symbolLabel: {
    fontWeight:  'bold',
    fontSize:    '13px',
    color:       '#131722',
    marginRight: 4,
  },
  divider: {
    display:         'inline-block',
    width:           '1px',
    height:          '16px',
    backgroundColor: '#ddd',
    margin:          '0 4px',
  },
  btn: {
    padding:         '2px 8px',
    fontSize:        '11px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
    lineHeight:      '18px',
  },
  btnActive: {
    padding:         '2px 8px',
    fontSize:        '11px',
    backgroundColor: '#2962ff',
    color:           '#ffffff',
    border:          '1px solid #2962ff',
    borderRadius:    '3px',
    cursor:          'pointer',
    lineHeight:      '18px',
  },
  chartArea: {
    position:  'relative',
    flex:      1,
    minHeight: 0,
    width:     '100%',
  },
  chartContainer: {
    position: 'absolute',
    inset:    0,
  },
  legend: {
    position:        'absolute',
    top:             8,
    left:            8,
    zIndex:          10,
    backgroundColor: 'rgba(255,255,255,0.85)',
    padding:         '3px 8px',
    borderRadius:    '3px',
    fontSize:        '11px',
    color:           '#333',
    display:         'flex',
    flexWrap:        'wrap',
    gap:             '6px',
    pointerEvents:   'none',
  },
  legendOHLC: {
    marginRight: 8,
    fontSize:    '11px',
    color:       '#333',
  },
  loading: {
    fontSize:   '11px',
    color:      '#999',
    marginLeft: 8,
  },
  errorText: {
    fontSize:   '11px',
    color:      '#ef5350',
    marginLeft: 8,
  },
  dateInput: {
    padding:      '2px 6px',
    fontSize:     '11px',
    border:       '1px solid #ddd',
    borderRadius: '3px',
    color:        '#444',
    lineHeight:   '18px',
    cursor:       'pointer',
  },
};

const modal = {
  overlay: {
    position:        'fixed',
    inset:           0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex:          1000,
    display:         'flex',
    alignItems:      'flex-start',
    justifyContent:  'center',
    paddingTop:      '80px',
  },
  box: {
    backgroundColor: '#ffffff',
    borderRadius:    '6px',
    boxShadow:       '0 8px 24px rgba(0,0,0,0.18)',
    minWidth:        '260px',
    overflow:        'hidden',
  },
  header: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'space-between',
    padding:         '12px 16px',
    borderBottom:    '1px solid #eee',
    backgroundColor: '#f7f7f7',
  },
  title: {
    fontWeight: 'bold',
    fontSize:   '14px',
    color:      '#131722',
  },
  closeBtn: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '14px',
    color:      '#666',
  },
  section: {
    padding:      '12px 16px',
    borderBottom: '1px solid #f0f0f0',
  },
  sectionLabel: {
    fontSize:      '11px',
    fontWeight:    'bold',
    color:         '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom:  '8px',
  },
  checkRow: {
    display:      'flex',
    alignItems:   'center',
    marginBottom: '6px',
    cursor:       'pointer',
  },
  colorDot: {
    display:      'inline-block',
    width:        '10px',
    height:       '10px',
    borderRadius: '50%',
    marginRight:  '8px',
    flexShrink:   0,
  },
  checkLabel: {
    fontSize: '13px',
    color:    '#333',
  },
  radioRow: {
    display: 'flex',
    gap:     '16px',
  },
  radioLabel: {
    display:    'flex',
    alignItems: 'center',
    fontSize:   '13px',
    color:      '#333',
    cursor:     'pointer',
  },
  styleRow: {
    display:      'flex',
    alignItems:   'center',
    marginBottom: '8px',
    gap:          '8px',
  },
  checkRowInline: {
    display:    'flex',
    alignItems: 'center',
    flex:       1,
    cursor:     'pointer',
    fontSize:   '13px',
    color:      '#333',
    minWidth:   '90px',
  },
  colorPicker: {
    width:        '28px',
    height:       '24px',
    border:       '1px solid #ddd',
    borderRadius: '3px',
    cursor:       'pointer',
    padding:      '1px',
    background:   'none',
  },
  widthBtns: {
    display: 'flex',
    gap:     '2px',
  },
  widthBtn: {
    width:           '22px',
    height:          '22px',
    fontSize:        '11px',
    backgroundColor: '#f0f0f0',
    color:           '#555',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
    padding:         0,
  },
  widthBtnActive: {
    backgroundColor: '#2962ff',
    color:           '#fff',
    borderColor:     '#2962ff',
  },
};

export default Chart;